/**
 * What a person types to find somebody: a phone, the end of one, a name.
 * That is personal data, and an address is no place for it: it would stay in
 * the browser's history, travel in a copied link and be written to a
 * gateway's log. So a list that is searched by a person keeps its search
 * here, in memory, a list each, and its address holds the rest of the view
 * (the tab, the page, the filters that name nobody).
 *
 * In memory for as long as the tab lives: the search is still there when the
 * person comes back from a row, and is gone after a reload or a sign-out.
 * Nothing is written to any storage.
 */
import { useCallback, useSyncExternalStore } from "react";

/** The lists searched by a person, each with a search of its own. */
export type PrivateSearchKey =
  | "orders"
  | "specialists"
  | "clients"
  | "staff"
  | "support"
  | "wallets"
  | "ledger"
  | "topups"
  | "partners"
  | "sms";

const kept = new Map<PrivateSearchKey, string>();
const listeners = new Set<() => void>();

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

function tell(): void {
  for (const notify of listeners) notify();
}

export function getPrivateSearch(key: PrivateSearchKey): string | undefined {
  return kept.get(key);
}

/** Sets a list's search; nothing, or only spaces, clears it. */
export function setPrivateSearch(key: PrivateSearchKey, value: string | undefined): void {
  const words = value?.trim() ?? "";
  if ((kept.get(key) ?? "") === words) return;
  if (words === "") kept.delete(key);
  else kept.set(key, words);
  tell();
}

/** Forgets every search: the person signed out. */
export function clearPrivateSearches(): void {
  if (kept.size === 0) return;
  kept.clear();
  tell();
}

/** A list's search and the way to set it. */
export function usePrivateSearch(
  key: PrivateSearchKey,
): [value: string | undefined, set: (value: string | undefined) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => kept.get(key),
    () => undefined,
  );
  const set = useCallback(
    (next: string | undefined) => {
      setPrivateSearch(key, next);
    },
    [key],
  );
  return [value, set];
}
